import React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Html } from 'react-konva-utils';
import Draggable from 'react-draggable';

import { FaCheck } from 'react-icons/fa';
import { BiSolidError } from 'react-icons/bi';
import { IoMdClose } from 'react-icons/io';
import type { FileMRZ } from '../../../../types/api.types';
import { useAnalysisActions } from '../../../../hooks/useAnalysisActions';
import type { HandleToggleChangeProps } from '@/features/analysis/types/canvas.types';
import { useTour } from '@reactour/tour';

const DEFAULT_INPUT_WIDTH = 600;

interface CanvasTooltipMRZProps {
  handleToggleChange: HandleToggleChangeProps;
  rows: FileMRZ;
}

export const CanvasTooltipMRZ = ({
  handleToggleChange,
  rows,
}: CanvasTooltipMRZProps) => {
  const [maxWidth, setMaxWidth] = useState(DEFAULT_INPUT_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const [inputValues, setInputValues] = useState<string[]>(rows);
  const [resultMRZ, setResultMRZ] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const textWidthRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { checkMRZ } = useAnalysisActions();

  const verifyMRZ = async () => {
    setIsLoading(true);
    try {
      const result = await checkMRZ(inputValues);
      setResultMRZ(result);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    verifyMRZ();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragStart = () => setIsDragging(true);
  const handleDragStop = () => setIsDragging(false);

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const sumIndex = inputRefs.current.length - 1;
    const currentIndex = index;

    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'Enter' ||
      event.key === 'Escape'
    ) {
      event.preventDefault();
    }

    if (event.key === 'ArrowDown') {
      if (currentIndex >= sumIndex) {
        return;
      }
      const currentInput = inputRefs.current[currentIndex];
      const nextInput = inputRefs.current[currentIndex + 1];

      if (currentInput && nextInput) {
        const position = currentInput.selectionStart;
        nextInput.setSelectionRange(position, position);
        nextInput.focus();
      }
    }

    if (event.key === 'ArrowUp') {
      if (currentIndex <= 0) {
        return;
      }
      const currentInput = inputRefs.current[currentIndex];
      const prevInput = inputRefs.current[currentIndex - 1];

      if (currentInput && prevInput) {
        const position = currentInput.selectionStart;
        prevInput.setSelectionRange(position, position);
        prevInput.focus();
      }
    }

    if (event.key === 'Escape') {
      const target = event.target as HTMLInputElement;
      target.blur();
    }

    event.key === 'Enter' && verifyMRZ();
  };

  const handleChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValues = [...inputValues];
    newValues[index] = event.target.value.toUpperCase();
    setInputValues(newValues);
  };

  const calculateMaxWidth = () => {
    setTimeout(() => {
      const widths = textWidthRefs.current.map((span) => {
        return span ? span.offsetWidth : 0;
      });
      const MAX_INPUT_WIDTH = Math.max(...widths);
      const finalMaxWidth =
        MAX_INPUT_WIDTH > DEFAULT_INPUT_WIDTH
          ? MAX_INPUT_WIDTH
          : DEFAULT_INPUT_WIDTH;
      setMaxWidth(finalMaxWidth);
    }, 1);
  };

  useEffect(() => {
    setInputValues(rows);
  }, [rows]);

  useEffect(() => {
    calculateMaxWidth();
  }, [inputValues]);

  // Tour logic

  const { isOpen, currentStep, setCurrentStep } = useTour();

  useEffect(() => {
    if (isOpen) {
      if (currentStep === 6 && resultMRZ) {
        setTimeout(() => {
          setCurrentStep(7);
        }, 1000);
      }
    }
  }, [isOpen, currentStep, resultMRZ, setCurrentStep]);

  return (
    <Html>
      <Draggable
        cancel=".no-drag"
        onStart={handleDragStart}
        onStop={handleDragStop}
      >
        <div
          id="mrz-modal"
          className={`absolute cursor-move rounded-md border border-neutralText border-opacity-20 bg-neutralBG dark:border-neutralText-dark dark:border-opacity-20 dark:bg-neutralBG-dark`}
        >
          <button
            data-tooltip-content="Revenir au mode aperçu"
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-delay-show={100}
            data-tooltip-offset={0}
            onClick={() => handleToggleChange('preview')}
            className="no-drag absolute -right-5 -top-5 flex h-10 w-10 items-center justify-center rounded-full bg-[#efefef] opacity-90 transition-all duration-100 hover:bg-[#e2e2e2] hover:opacity-100 dark:bg-[#333135] dark:hover:bg-[#545454]"
            id="mrz-modal-close-button"
          >
            <IoMdClose className="text-2xl" />
          </button>
          <div className="rounded-t-md bg-[#f6f6f6] p-4 font-ocr dark:bg-[#242424]">
            {inputValues?.length > 0 &&
              inputValues.map((item, index) => (
                <div
                  className="border-x border-b border-neutralText border-opacity-20 first:rounded-t-md first:border-x first:border-t last:rounded-b-md dark:border-neutralText-dark dark:border-opacity-20"
                  key={index}
                >
                  <div>
                    <span
                      ref={(el) => (textWidthRefs.current[index] = el)}
                      className="text-md invisible absolute border px-2.5 uppercase tracking-[0.4em]"
                    >
                      {item}
                    </span>
                    <input
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      className={`no-drag text-md border-none border-neutralText border-opacity-20 bg-[transparent] pl-2.5 pr-0 uppercase tracking-[0.4em] text-neutralText dark:border-neutralText-dark dark:border-opacity-20 dark:text-neutralText-dark ${
                        isDragging ? 'pointer-events-none' : ''
                      }`}
                      autoComplete="off"
                      autoCorrect="off"
                      value={item}
                      onChange={(event) => handleChange(index, event)}
                      onKeyDown={(event) => handleKeyDown(index, event)}
                      style={{ width: maxWidth + 5 + 'px' }}
                    />
                  </div>
                </div>
              ))}
          </div>
          <div className="flex cursor-move items-center justify-between p-4">
            <button
              disabled={isLoading ? true : false}
              type="submit"
              onClick={verifyMRZ}
              className="no-drag rounded-md bg-primary px-5 py-2 font-normal text-neutralBG transition-all duration-100 hover:bg-primaryHover dark:bg-primary-dark dark:hover:bg-primaryHover-dark"
            >
              Re-vérifier le MRZ
            </button>
            <div className="flex items-center rounded-md uppercase">
              {isLoading ? (
                <svg
                  width="38"
                  height="38"
                  viewBox="0 0 38 38"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-[40px] w-[40px] stroke-[#161616] opacity-80 dark:stroke-[#eaeaea]"
                >
                  <g fill="none" fillRule="evenodd">
                    <g transform="translate(1 1)" strokeWidth="2">
                      <circle strokeOpacity=".5" cx="18" cy="18" r="18" />
                      <path d="M36 18c0-9.94-8.06-18-18-18">
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 18 18"
                          to="360 18 18"
                          dur="1s"
                          repeatCount="indefinite"
                        />
                      </path>
                    </g>
                  </g>
                </svg>
              ) : (
                <>
                  <span className="flex">
                    {resultMRZ ? (
                      <FaCheck className="mr-2.5 text-xl text-successText" />
                    ) : (
                      <BiSolidError className="mr-2.5 text-xl text-errorText-dark" />
                    )}
                  </span>
                  <span
                    className={`opacity-90 ${
                      resultMRZ ? 'text-successText' : 'text-errorText-dark'
                    }`}
                  >
                    {' '}
                    MRZ {resultMRZ ? 'VALIDE' : 'FAUSSE'}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </Draggable>
    </Html>
  );
};

export default CanvasTooltipMRZ;
